import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Input, Select, Tag, message, notification } from "antd";
import { IoMdCloseCircle } from "react-icons/io";
import PageHeader from "../../component/pageHeaders";
import Loadingbox from "../../component/loadingbox";
import DataTable from "./components/datatable";
import { API } from "../../config/API";
import { GET, PATCH, DELETE } from "../../utils/apirequest";

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  archived: "Archived",
};

const REGION_LABELS: Record<string, string> = {
  india: "India",
  gulf: "Gulf",
};

function SubscriptionPlanScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [indicator, setIndicator] = useState<boolean>(true);
  const [page, setPage] = useState<number>(Number(query.get("page")) || 1);
  const [pageSize, setPageSize] = useState<number>(Number(query.get("limit")) || 10);
  const [filter, setFilter] = useState<any>({});
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    setPage(Number(query.get("page")) || 1);
    setPageSize(Number(query.get("limit")) || 10);
  }, [query]);

  useEffect(() => {
    loadData(page, pageSize, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filter]);

  const loadData = async (
    p: number = page,
    size: number = pageSize,
    f: any = filter
  ) => {
    setIndicator(true);
    try {
      const params = new URLSearchParams({
        include_inactive: "true",
        page: String(p),
        pageSize: String(size),
      });
      if (f.search) params.set("search", String(f.search));
      if (f.status) params.set("status", String(f.status));
      if (f.type) params.set("type", String(f.type));
      if (f.region) params.set("region", String(f.region));

      const res: any = await GET(
        `${API.SUBSCRIPTION_PLANS}?${params.toString()}`,
        null,
        null
      );
      const rows = Array.isArray(res?.data) ? res.data : [];
      setData(rows);
      setTotal(Number(res?.pagination?.total) || rows.length);
    } catch (err) {
      console.log("err", err);
      message.error("Could not load subscription plans.");
    } finally {
      setIsLoading(false);
      setIndicator(false);
    }
  };

  const goToPage = (p: number, size: number) => {
    query.set("page", String(p));
    query.set("limit", String(size));
    navigate({ search: query.toString() });
  };

  const updateFilter = (next: any) => {
    setFilter(next);
    if (Number(page) !== 1) goToPage(1, pageSize);
  };

  const Search = (searchText: any) => {
    if (!searchText) {
      const next = { ...filter };
      delete next.search;
      updateFilter(next);
      return;
    }
    if (searchText.length > 1) {
      updateFilter({ ...filter, search: searchText });
    }
  };

  const LoadByStatus = (val: any) => {
    const next = { ...filter };
    if (val) next.status = val;
    else delete next.status;
    updateFilter(next);
  };

  const LoadByType = (val: any) => {
    const next = { ...filter };
    if (val) next.type = val;
    else delete next.type;
    updateFilter(next);
  };

  const LoadByRegion = (val: any) => {
    const next = { ...filter };
    if (val) next.region = val;
    else delete next.region;
    updateFilter(next);
  };

  const clearFilters = (key: any) => {
    const updated = { ...filter };
    delete updated[key];
    updateFilter(updated);
  };

  const onPageChange = (p: any, size: any) => {
    goToPage(Number(p), Number(size));
    window.scrollTo(0, 0);
  };

  const deleteData = async (id: any) => {
    setIsLoading(true);
    try {
      const res: any = await DELETE(`${API.SUBSCRIPTION_PLAN_ONE}${id}`);
      if (res?.error) {
        message.error(res.error.message || "Failed to delete plan");
      } else {
        message.success("Plan deleted");
      }
      await loadData(page, pageSize, filter);
    } catch (err: any) {
      const msg = err?.error?.message || err?.data?.error?.message || "Could not delete plan.";
      message.error(msg);
      setIsLoading(false);
    }
  };

  const toggleStatus = async (row: any) => {
    setTogglingId(row.id);
    const toArchive = row.status !== "archived";
    const url = toArchive
      ? `${API.SUBSCRIPTION_PLAN_ARCHIVE}${row.id}/archive`
      : `${API.SUBSCRIPTION_PLAN_ACTIVATE}${row.id}/activate`;
    try {
      await PATCH(url, {});
      notification.success({
        message: toArchive ? "Archived" : "Activated",
        description: toArchive
          ? "Plan archived and hidden from forms."
          : "Plan is now active.",
      });
      await loadData(page, pageSize, filter);
    } catch (err: any) {
      const msg = err?.error?.message || "Could not update plan status.";
      notification.error({ message: "Sorry", description: msg });
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title={"Subscription Plans"}
        loader={indicator}
        length={`Dashboard / Subscription Plans`}
      >
        <div className="homescreen-pageItem">
          <Input
            style={{ width: "250px" }}
            allowClear
            placeholder="Search by name, type, description"
            onChange={(e: any) => Search(e.target.value)}
          />
          &nbsp;
          <Select
            placeholder="Status"
            onChange={(val) => LoadByStatus(val)}
            style={{ width: 150 }}
            allowClear
          >
            <Select.Option value="active">Active</Select.Option>
            <Select.Option value="archived">Archived</Select.Option>
          </Select>
          &nbsp;
          <Select
            placeholder="Type"
            onChange={(val) => LoadByType(val)}
            style={{ width: 170 }}
            allowClear
          >
            <Select.Option value="free">Free</Select.Option>
            <Select.Option value="trial">Trial</Select.Option>
            <Select.Option value="monthly">Monthly</Select.Option>
            <Select.Option value="quarterly">Quarterly</Select.Option>
            <Select.Option value="yearly">Yearly</Select.Option>
            <Select.Option value="print_combo">Print Combo</Select.Option>
            <Select.Option value="custom">Custom</Select.Option>
          </Select>
          &nbsp;
          <Select
            placeholder="Region"
            onChange={(val) => LoadByRegion(val)}
            style={{ width: 130 }}
            allowClear
          >
            <Select.Option value="india">India</Select.Option>
            <Select.Option value="gulf">Gulf</Select.Option>
          </Select>
          &nbsp;
          <div>
            <Button
              type="primary"
              onClick={() => navigate("/Auth/subscription-plans/form/create")}
            >
              Add New +
            </Button>
          </div>
        </div>
      </PageHeader>
      <div style={{ padding: 5 }} />
      <div className="PageHeaders-box5">
        <div className="PageHeaders-txt3">Plans ( {total} )</div>
        {Object.keys(filter)?.length ? <div>|</div> : null}
        <div className="PageHeaders-txt2">
          {Object.keys(filter)?.length ? "Filter By : " : ``}
        </div>
        <div>
          {Object.keys(filter)?.map((key) => (
            <Tag
              key={key}
              onClick={() => clearFilters(key)}
              icon={<IoMdCloseCircle color="red" />}
            >
              &nbsp;{key} : {key === "status"
                ? STATUS_LABELS[String(filter[key])] || filter[key]
                : key === "region"
                ? REGION_LABELS[String(filter[key])] || filter[key]
                : filter[key]}
            </Tag>
          ))}
        </div>
      </div>
      {isLoading ? (
        <Loadingbox />
      ) : (
        <DataTable
          data={data}
          meta={total}
          page={page}
          pagesize={pageSize}
          onDelete={(id: any) => deleteData(id)}
          onToggleStatus={(row: any) => toggleStatus(row)}
          togglingId={togglingId}
          onPageChange={(p: any, s: any) => onPageChange(p, s)}
        />
      )}
    </div>
  );
}

export default SubscriptionPlanScreen;
